import { useCallback, useEffect, useRef, useState } from 'react';
import { EASYRTC_URL } from '@/lib/config';
import { getValidAccessToken } from '@/lib/api';
import {
  awaitEasyRtcIdle,
  loadEasyRtc,
  safeUsername,
  tuneCameraSender,
  tuneScreenSender,
  withAudioProcessing,
  type EasyRtc,
} from './easyrtcClient';
import {
  summarizeStats,
  UNKNOWN_QUALITY,
  type PeerQuality,
  type StatsSample,
} from './connectionQuality';
import { SpeakingMonitor } from './speakingMonitor';

export interface EasyRtcPeer {
  easyrtcid: string;
  name: string;
  streams: { camera?: MediaStream; screen?: MediaStream };
  /** Live link health, refreshed by the stats poll. */
  quality: PeerQuality;
  /** True while this peer's audio carries speech-level energy. */
  speaking: boolean;
}

/** Which physical devices to capture from. Empty means the browser default. */
export interface DevicePreference {
  cameraId?: string;
  microphoneId?: string;
}

export interface EasyRtcState {
  status: 'idle' | 'loading' | 'connecting' | 'connected' | 'error';
  myId: string | null;
  peers: EasyRtcPeer[];
  localStream: MediaStream | null;
  screenStream: MediaStream | null;
  micOn: boolean;
  camOn: boolean;
  screenOn: boolean;
  speakingSelf: boolean;
  /**
   * How our own upload is faring. Distinct from any peer's quality: it is the
   * one reading that points at this user's own network or machine.
   */
  localQuality: PeerQuality;
  error: string | null;
}

/** How often peer connections are polled for stats. */
const STATS_INTERVAL_MS = 3000;

/** Key the speaking monitor uses for our own microphone. */
const SELF = '__self__';

/** Orders quality scores so the worst link in the room can be picked out. */
function rank(score: PeerQuality['score']): number {
  return score === 'poor' ? 2 : score === 'fair' ? 1 : 0;
}

const SCREEN_STREAM = 'screen';
const CAMERA_STREAM = 'default';

/** Where EasyRTC parks a connection that joined without naming a room. */
const DEFAULT_ROOM = 'default';

/**
 * Drives an EasyRTC mesh meeting.
 *
 * Mesh calling needs a tie-break: if both peers call each other you get two
 * connections for one conversation. The lower easyrtcid always places the
 * call, which every peer can decide locally with no extra signalling.
 */
export function useEasyRtc({
  meetingId,
  displayName,
  orgId,
  /**
   * Which kind of room this is. Calls are authorised against the live call
   * registry rather than a meeting document, so the signalling service needs
   * to be told which one to check.
   */
  kind = 'meeting',
  /** Start with the camera off — a voice call that opens on video is rude. */
  startWithVideo = true,
  /** External meetings exchange their opaque invite for this meeting-only token. */
  guestSessionToken,
  /** Capture the microphone, but honour mute-on-entry before joining the mesh. */
  startWithAudio = true,
  /**
   * Chosen camera and microphone. Changing these after the call is up swaps
   * the track in place rather than restarting the session — see `useDevice`.
   */
  devices,
}: {
  meetingId: string | null;
  displayName: string;
  orgId: string | null;
  kind?: 'meeting' | 'call';
  startWithVideo?: boolean;
  guestSessionToken?: string;
  startWithAudio?: boolean;
  devices?: DevicePreference;
}) {
  const [state, setState] = useState<EasyRtcState>({
    status: 'idle',
    myId: null,
    peers: [],
    localStream: null,
    screenStream: null,
    micOn: true,
    camOn: true,
    screenOn: false,
    speakingSelf: false,
    localQuality: UNKNOWN_QUALITY,
    error: null,
  });

  const rtcRef = useRef<EasyRtc | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenRef = useRef<MediaStream | null>(null);
  const myIdRef = useRef<string | null>(null);
  const calledRef = useRef<Set<string>>(new Set());
  const occupantsRef = useRef<string[]>([]);
  const cameraProfileRef = useRef<'small' | 'medium' | 'large' | null>(null);
  const speakingRef = useRef<SpeakingMonitor | null>(null);
  /** Previous stats counters per peer, so each poll can report a rate. */
  const statsRef = useRef(new Map<string, StatsSample>());
  /**
   * Read at capture time only. Keeping the chosen devices out of the effect's
   * dependencies is what stops picking a different microphone from tearing
   * down the whole session and rejoining the room.
   */
  const devicesRef = useRef<DevicePreference>(devices ?? {});
  devicesRef.current = devices ?? {};

  /**
   * Which of our local streams a new peer connection should carry.
   *
   * EasyRTC only attaches the streams named at the moment the connection is
   * negotiated, so a share that is already running has to be named up front —
   * both when we place the call and when we answer one.
   */
  const streamsToSend = useCallback(
    () => (screenRef.current ? [CAMERA_STREAM, SCREEN_STREAM] : [CAMERA_STREAM]),
    [],
  );

  const upsertPeer = useCallback((easyrtcid: string, patch: Partial<EasyRtcPeer>) => {
    setState((s) => {
      const index = s.peers.findIndex((p) => p.easyrtcid === easyrtcid);
      const base: EasyRtcPeer = s.peers[index] ?? {
        easyrtcid,
        name: rtcRef.current?.idToName(easyrtcid) ?? 'Guest',
        streams: {},
        quality: UNKNOWN_QUALITY,
        speaking: false,
      };
      const merged: EasyRtcPeer = {
        ...base,
        ...patch,
        streams: { ...base.streams, ...patch.streams },
      };
      const peers = [...s.peers];
      if (index === -1) peers.push(merged);
      else peers[index] = merged;
      return { ...s, peers };
    });
  }, []);

  /**
   * Mesh upload cost grows once per peer. Reduce camera pixels and frames as
   * the room grows, while leaving screen sharing on its separate detail-first
   * profile. Applying a new profile only when the tier changes avoids encoder
   * churn when the occupant roster is repeated.
   */
  const tuneCameraForRoom = useCallback((participantCount: number) => {
    const profile =
      participantCount >= 7 ? 'small' : participantCount >= 4 ? 'medium' : 'large';
    if (cameraProfileRef.current === profile) return;
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    cameraProfileRef.current = profile;
    const constraints: MediaTrackConstraints =
      profile === 'small'
        ? {
            width: { ideal: 320, max: 480 },
            height: { ideal: 180, max: 270 },
            frameRate: { ideal: 12, max: 15 },
          }
        : profile === 'medium'
          ? {
              width: { ideal: 480, max: 640 },
              height: { ideal: 270, max: 360 },
              frameRate: { ideal: 15, max: 18 },
            }
          : {
              width: { ideal: 960, max: 960 },
              height: { ideal: 540, max: 540 },
              frameRate: { ideal: 24, max: 24 },
            };
    void track.applyConstraints(constraints).catch(() => {
      // Some embedded browsers expose a fixed camera profile. Keep the stream
      // alive rather than treating an optional quality adjustment as fatal.
    });
  }, []);

  useEffect(() => {
    if (!meetingId || (!orgId && !guestSessionToken)) return;

    let cancelled = false;
    setState((s) => ({
      ...s,
      status: 'loading',
      error: null,
      micOn: startWithAudio,
      camOn: startWithVideo,
    }));

    async function start() {
      try {
        const accessToken = guestSessionToken ? null : await getValidAccessToken();
        if (!guestSessionToken && !accessToken) {
          throw new Error('Your session has expired — please sign in again');
        }

        const easyrtc = await loadEasyRtc();
        if (cancelled) return;
        rtcRef.current = easyrtc;

        speakingRef.current = new SpeakingMonitor((speaking) => {
          if (cancelled) return;
          setState((s) => ({
            ...s,
            speakingSelf: speaking.has(SELF),
            peers: s.peers.map((peer) =>
              peer.speaking === speaking.has(peer.easyrtcid)
                ? peer
                : { ...peer, speaking: speaking.has(peer.easyrtcid) },
            ),
          }));
        });

        easyrtc.setSocketUrl(EASYRTC_URL, { path: '/easyrtc/socket.io' });
        easyrtc.setCredential(
          guestSessionToken
            ? { guestToken: guestSessionToken, meetingId }
            : {
                token: accessToken,
                orgId,
                ...(kind === 'call' ? { callId: meetingId } : { meetingId }),
              },
        );
        easyrtc.setUsername(safeUsername(displayName));
        easyrtc.enableDataChannels(false);
        easyrtc.enableAudio(true);
        easyrtc.enableVideo(startWithVideo);
        // Both must precede initMediaSource: EasyRTC folds them into the
        // constraints it builds for that one capture.
        if (devicesRef.current.cameraId) easyrtc.setVideoSource(devicesRef.current.cameraId);
        if (devicesRef.current.microphoneId) {
          easyrtc.setAudioSource(devicesRef.current.microphoneId);
        }
        withAudioProcessing(easyrtc);
        // Keep enough upstream capacity available for a screen share. A
        // browser may otherwise capture a 1080p/4K camera and upload it beside
        // the presentation to every peer in this mesh call.
        easyrtc.setVideoDims(960, 540, 24);
        easyrtc.enableAudioReceive(true);
        easyrtc.enableVideoReceive(true);

        easyrtc.setOnError(({ errorText }) => {
          if (cancelled) return;
          setState((s) => ({ ...s, error: errorText }));
        });

        easyrtc.setDisconnectListener(() => {
          if (cancelled) return;
          setState((s) => ({ ...s, status: 'idle', peers: [], myId: null }));
        });

        // Shares already active when a peer connects bypass addStreamToCall's
        // acknowledgement, so tune that sender when the connection opens too.
        easyrtc.setPeerOpenListener((easyrtcid) => {
          const repairCameraSender = async () => {
            const stream = localStreamRef.current;
            const cameraTrack = stream?.getVideoTracks()[0];
            const peerConnection = easyrtc.getPeerConnectionByUserId(easyrtcid);
            if (!stream || !cameraTrack || !peerConnection) return;
            const sender = peerConnection
              .getSenders()
              .find((candidate) => candidate.track?.kind === 'video');
            if (!sender) {
              // Older WebKit can complete the answer without attaching its
              // local camera. Adding the named stream triggers one clean
              // renegotiation after ICE is stable.
              easyrtc.addStreamToCall(easyrtcid, CAMERA_STREAM);
              return;
            }
            // Reinforce both the live source and its MediaStream association.
            // The latter ensures the remote `track` event contains a stream,
            // which open-easyrtc requires before it calls streamAcceptor.
            sender.setStreams(stream);
            if (sender.track !== cameraTrack) await sender.replaceTrack(cameraTrack);
            await tuneCameraSender(
              easyrtc,
              easyrtcid,
              cameraTrack,
              Math.max(1, occupantsRef.current.length),
              Boolean(screenRef.current),
            );
          };
          void repairCameraSender();
          window.setTimeout(() => void repairCameraSender(), 750);

          const track = screenRef.current?.getVideoTracks()[0];
          if (!track) return;
          const peerCount = Math.max(1, occupantsRef.current.length);
          void tuneScreenSender(easyrtc, easyrtcid, track, peerCount);
          window.setTimeout(() => {
            if (screenRef.current?.getVideoTracks()[0] === track) {
              void tuneScreenSender(easyrtc, easyrtcid, track, peerCount);
            }
          }, 750);
        });

        easyrtc.setStreamAcceptor((easyrtcid, stream, streamName) => {
          if (cancelled) return;
          upsertPeer(easyrtcid, {
            name: easyrtc.idToName(easyrtcid),
            streams: streamName === SCREEN_STREAM ? { screen: stream } : { camera: stream },
          });
          // Voice rides on the camera stream; a shared screen's audio is the
          // presenter's tab, not them talking.
          if (streamName !== SCREEN_STREAM) {
            speakingRef.current?.track(easyrtcid, stream, { remote: true });
          }
        });

        // Peers in a meeting room are already authenticated, so every offer is
        // accepted — but the answer has to name our screen stream too, or a
        // share we started before they arrived would never reach them.
        easyrtc.setAcceptChecker((_easyrtcid, acceptor) => {
          acceptor(true, streamsToSend());
        });

        easyrtc.setOnStreamClosed((easyrtcid, _stream, streamName) => {
          if (cancelled) return;
          if (streamName !== SCREEN_STREAM) speakingRef.current?.untrack(easyrtcid);
          setState((s) => ({
            ...s,
            peers: s.peers
              .map((peer) => {
                if (peer.easyrtcid !== easyrtcid) return peer;
                const streams = { ...peer.streams };
                if (streamName === SCREEN_STREAM) delete streams.screen;
                else delete streams.camera;
                return { ...peer, streams };
              }),
          }));
        });

        easyrtc.setRoomOccupantListener((_roomName, occupants) => {
          if (cancelled) return;
          const ids = Object.keys(occupants);
          const me = myIdRef.current;
          occupantsRef.current = ids;
          tuneCameraForRoom(ids.length + 1);

          for (const id of ids) {
            if (!me || id === me) continue;
            // Lower id calls higher id, so exactly one offer is made.
            if (me > id) continue;
            if (calledRef.current.has(id)) continue;
            if (easyrtc.getConnectStatus(id) !== easyrtc.NOT_CONNECTED) continue;

            calledRef.current.add(id);
            easyrtc.call(
              id,
              undefined,
              (_code, text) => {
                calledRef.current.delete(id);
                console.warn('[easyrtc] call failed', text);
              },
              undefined,
              streamsToSend(),
            );
          }

          // Forget anyone who left so a rejoin can be called again.
          for (const id of [...calledRef.current]) {
            if (!ids.includes(id)) calledRef.current.delete(id);
          }
          // Someone who left keeps neither an analyser nor a stats baseline.
          for (const id of [...statsRef.current.keys()]) {
            if (!ids.includes(id)) {
              statsRef.current.delete(id);
              speakingRef.current?.untrack(id);
            }
          }

          setState((s) => ({
            ...s,
            // The EasyRTC roster is authoritative for presence. Keep an
            // entry even when a mobile guest has no camera stream yet, so
            // they appear as an avatar instead of being counted but invisible.
            peers: ids
              .filter((id) => id !== me)
              .map((id) => {
                const current = s.peers.find((peer) => peer.easyrtcid === id);
                return {
                  easyrtcid: id,
                  name: occupants[id]?.username || current?.name || easyrtc.idToName(id) || 'Guest',
                  streams: current?.streams ?? {},
                  quality: current?.quality ?? UNKNOWN_QUALITY,
                  speaking: current?.speaking ?? false,
                };
              }),
          }));
        });

        /**
         * `joinRoom` signals over the socket, so it throws outright if it runs
         * before `connect` has opened one — the room has to follow the
         * handshake, not precede it.
         */
        const enterRoom = (easyrtcid: string) => {
          if (cancelled) return;
          myIdRef.current = easyrtcid;
          easyrtc.joinRoom(
            meetingId!,
            null,
            () => {
              if (cancelled) return;
              // Connecting without naming a room lands us in the app's default
              // one, where every other meeting would be on the same roster.
              try {
                easyrtc.leaveRoom(DEFAULT_ROOM);
              } catch {
                // Not in it — nothing to leave.
              }
              setState((s) => ({ ...s, status: 'connected', myId: easyrtcid }));
            },
            (_code, text) => {
              if (cancelled) return;
              console.warn('[easyrtc] joinRoom failed', text);
              setState((s) => ({ ...s, status: 'error', error: text }));
            },
          );
        };

        /**
         * Opens the signalling socket once the EasyRTC singleton is free.
         *
         * A meeting held over a floor is why this is not a bare `connect`: the
         * space's proximity mesh drives the same global, and it only lets go
         * as the panel opens — asynchronously. Connecting into that gap was
         * refused silently, which is what left this on "Connecting…" for ever.
         */
        const connectToSignalling = async () => {
          try {
            await awaitEasyRtcIdle(easyrtc);
          } catch (err) {
            if (cancelled) return;
            setState((s) => ({
              ...s,
              status: 'error',
              error: err instanceof Error ? err.message : 'Could not join this meeting',
            }));
            return;
          }
          if (cancelled) return;
          easyrtc.connect('itsappy', enterRoom, (_code, text) => {
            if (cancelled) return;
            setState((s) => ({ ...s, status: 'error', error: text }));
          });
        };

        setState((s) => ({ ...s, status: 'connecting' }));

        easyrtc.initMediaSource(
          (stream) => {
            if (cancelled) {
              // The permission prompt can resolve after the user has already
              // left the route. Never leave that late camera capture running.
              stream.getTracks().forEach((track) => track.stop());
              try {
                easyrtc.closeLocalMediaStream(CAMERA_STREAM);
              } catch {
                // The stream may not have finished registering yet.
              }
              return;
            }
            localStreamRef.current = stream;
            if (!startWithAudio) {
              stream.getAudioTracks().forEach((track) => (track.enabled = false));
              easyrtc.enableAudio(false);
            }
            const cameraTrack = stream.getVideoTracks()[0];
            if (cameraTrack && 'contentHint' in cameraTrack) cameraTrack.contentHint = 'motion';
            // Local capture needs no media element to keep it flowing, and
            // attaching one would play the user's own microphone back at them.
            speakingRef.current?.track(SELF, stream, { remote: false });
            if (!startWithAudio) speakingRef.current?.setMuted(SELF, true);
            setState((s) => ({ ...s, localStream: stream }));

            void connectToSignalling();
          },
          (_code, text) => {
            if (cancelled) return;
            // No camera or mic is not fatal — join as a viewer.
            console.warn('[easyrtc] media unavailable', text);
            setState((s) => ({ ...s, micOn: false, camOn: false }));
            void connectToSignalling();
          },
        );
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          status: 'error',
          error: err instanceof Error ? err.message : 'Could not start the meeting',
        }));
      }
    }

    void start();

    // Captured now so cleanup clears the collections this effect populated.
    const called = calledRef.current;
    const samples = statsRef.current;

    return () => {
      cancelled = true;
      const easyrtc = rtcRef.current;
      screenRef.current?.getTracks().forEach((track) => track.stop());
      screenRef.current = null;
      localStreamRef.current?.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
      called.clear();
      occupantsRef.current = [];
      myIdRef.current = null;
      cameraProfileRef.current = null;
      speakingRef.current?.destroy();
      speakingRef.current = null;
      samples.clear();
      if (easyrtc) {
        try {
          easyrtc.hangupAll();
        } catch {
          // A partially connected call may have nothing to hang up.
        }
        try {
          easyrtc.leaveRoom(meetingId!);
        } catch {
          // The room may not have finished joining.
        }
        try {
          easyrtc.closeLocalMediaStream(CAMERA_STREAM);
        } catch {
          // The local stream may already be closed.
        }
        try {
          easyrtc.disconnect();
        } catch {
          // The socket may never have finished connecting.
        }
      }
      rtcRef.current = null;
      setState({
        status: 'idle',
        myId: null,
        peers: [],
        localStream: null,
        screenStream: null,
        micOn: true,
        camOn: true,
        screenOn: false,
        speakingSelf: false,
        localQuality: UNKNOWN_QUALITY,
        error: null,
      });
    };
  }, [
    meetingId,
    orgId,
    displayName,
    kind,
    startWithVideo,
    guestSessionToken,
    startWithAudio,
    upsertPeer,
    streamsToSend,
    tuneCameraForRoom,
  ]);

  /* ------------------------------- stats -------------------------------- */

  /**
   * Polls every live peer connection and folds the result back onto its tile.
   *
   * This runs off the connection status rather than off `peers` so that
   * publishing a new quality reading cannot re-trigger the effect that
   * produced it. The list of connections to poll is read from the occupant
   * ref, which the roster listener keeps current.
   */
  useEffect(() => {
    if (state.status !== 'connected') return;

    const poll = async () => {
      const easyrtc = rtcRef.current;
      if (!easyrtc) return;
      const myId = myIdRef.current;
      const readings = new Map<string, PeerQuality>();

      for (const id of occupantsRef.current) {
        if (id === myId) continue;
        const connection = easyrtc.getPeerConnectionByUserId(id);
        if (!connection) continue;
        try {
          const report = await connection.getStats();
          const { quality, sample } = summarizeStats(report, statsRef.current.get(id) ?? null);
          statsRef.current.set(id, sample);
          readings.set(id, quality);
        } catch {
          // A connection torn down mid-poll simply has no reading this round.
        }
      }

      // Our own upload health is the same across peers, so any one connection
      // answers it. Loss and round-trip belong to a link, but the encoder's
      // limitation reason is a property of this machine.
      const limitation = [...readings.values()].find((entry) => entry.limitation)?.limitation;
      const worst = [...readings.values()].reduce<PeerQuality | null>(
        (worstSoFar, entry) =>
          !worstSoFar || rank(entry.score) > rank(worstSoFar.score) ? entry : worstSoFar,
        null,
      );

      setState((s) => ({
        ...s,
        localQuality: {
          ...(worst ?? UNKNOWN_QUALITY),
          limitation: limitation ?? null,
        },
        peers: s.peers.map((peer) => {
          const quality = readings.get(peer.easyrtcid);
          return quality ? { ...peer, quality } : peer;
        }),
      }));
    };

    const timer = window.setInterval(() => void poll(), STATS_INTERVAL_MS);
    void poll();
    return () => window.clearInterval(timer);
  }, [state.status]);

  /* ------------------------------ controls ------------------------------ */

  const toggleMic = useCallback(() => {
    setState((s) => {
      rtcRef.current?.enableAudio(!s.micOn);
      s.localStream?.getAudioTracks().forEach((track) => (track.enabled = !s.micOn));
      // A disabled track still carries silence to the analyser, but clearing
      // the hold means the ring drops the instant mute is pressed.
      speakingRef.current?.setMuted(SELF, s.micOn);
      return { ...s, micOn: !s.micOn, speakingSelf: s.micOn ? false : s.speakingSelf };
    });
  }, []);

  const toggleCam = useCallback(() => {
    setState((s) => {
      rtcRef.current?.enableVideo(!s.camOn);
      s.localStream?.getVideoTracks().forEach((track) => (track.enabled = !s.camOn));
      return { ...s, camOn: !s.camOn };
    });
  }, []);

  /**
   * Switches camera or microphone mid-call.
   *
   * The obvious implementation — recapture and rejoin — would drop everyone's
   * tile and re-run ICE for a change the room should never notice. Instead the
   * new track replaces the old one on every existing sender, which needs no
   * renegotiation at all, and is then swapped into the local stream so the
   * user's own preview follows.
   */
  const useDevice = useCallback(async (kindToSwitch: 'camera' | 'microphone', deviceId: string) => {
    const easyrtc = rtcRef.current;
    const stream = localStreamRef.current;
    if (!easyrtc || !stream) return;

    const isCamera = kindToSwitch === 'camera';
    let replacement: MediaStream;
    try {
      replacement = await navigator.mediaDevices.getUserMedia(
        isCamera
          ? { video: { deviceId: { exact: deviceId }, width: { ideal: 960 }, height: { ideal: 540 } } }
          : {
              audio: {
                deviceId: { exact: deviceId },
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
            },
      );
    } catch (err) {
      setState((s) => ({
        ...s,
        error:
          err instanceof Error
            ? `Could not switch ${kindToSwitch}: ${err.message}`
            : `Could not switch ${kindToSwitch}`,
      }));
      return;
    }

    const nextTrack = isCamera
      ? replacement.getVideoTracks()[0]
      : replacement.getAudioTracks()[0];
    if (!nextTrack) {
      replacement.getTracks().forEach((track) => track.stop());
      return;
    }

    // Carry the current mute state across, or switching device would quietly
    // un-mute someone.
    const previous = isCamera ? stream.getVideoTracks()[0] : stream.getAudioTracks()[0];
    nextTrack.enabled = previous?.enabled ?? true;
    if (isCamera && 'contentHint' in nextTrack) nextTrack.contentHint = 'motion';

    for (const id of occupantsRef.current) {
      if (id === myIdRef.current) continue;
      const sender = easyrtc
        .getPeerConnectionByUserId(id)
        ?.getSenders()
        .find((candidate) => candidate.track?.kind === nextTrack.kind);
      await sender?.replaceTrack(nextTrack).catch(() => undefined);
    }

    if (previous) {
      stream.removeTrack(previous);
      previous.stop();
    }
    stream.addTrack(nextTrack);

    // The analyser holds a source node bound to the old track, so a new
    // microphone needs the graph rebuilt or the ring would stop responding.
    if (!isCamera) {
      speakingRef.current?.track(SELF, stream, { remote: false });
      speakingRef.current?.setMuted(SELF, !nextTrack.enabled);
    }
    // The capture profile is chosen per device, so let the next roster update
    // re-apply it rather than carrying the old device's tier over.
    cameraProfileRef.current = null;
    setState((s) => ({ ...s, localStream: stream, error: null }));
  }, []);

  /**
   * EasyRTC's own `setScreenCapture` predates `getDisplayMedia` and its
   * constraints no longer work, so the display is captured here and handed to
   * EasyRTC as a named local stream.
   *
   * `register3rdPartyLocalMediaStream` is used rather than
   * `buildLocalMediaStream`, which can only compose a stream by cloning one
   * that already exists — that would leave anyone who joined without a camera
   * or microphone unable to present.
   */
  const toggleScreen = useCallback(async () => {
    const easyrtc = rtcRef.current;
    if (!easyrtc) return;

    // ---- stop presenting ----
    if (screenRef.current) {
      const display = screenRef.current;
      screenRef.current = null;
      try {
        // Detaches the stream from every peer connection and tells them it is
        // going away, which fires their onStreamClosed.
        easyrtc.closeLocalMediaStream(SCREEN_STREAM);
      } catch {
        // Already gone.
      }
      display.getTracks().forEach((track) => track.stop());
      const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
      if (cameraTrack) {
        for (const id of occupantsRef.current) {
          if (id !== myIdRef.current) {
            void tuneCameraSender(
              easyrtc,
              id,
              cameraTrack,
              Math.max(1, occupantsRef.current.length),
              false,
            );
          }
        }
      }
      setState((s) => ({ ...s, screenOn: false, screenStream: null }));
      return;
    }

    // ---- start presenting ----
    let display: MediaStream;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({
        // Capture enough pixels for readable text while keeping motion bounded
        // so a presentation cannot build a multi-second upload queue.
        video: {
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 12, max: 12 },
        },
        // Supported browsers can include audio from a shared tab. Browsers
        // that do not support it simply return a video-only stream.
        audio: true,
      });
    } catch (err) {
      // Dismissing the picker is a normal choice, not an error worth showing.
      if ((err as Error).name !== 'NotAllowedError') {
        setState((s) => ({
          ...s,
          error: err instanceof Error ? err.message : 'Screen share failed',
        }));
      }
      return;
    }

    // The hook may have torn down while the picker was open.
    if (!rtcRef.current) {
      display.getTracks().forEach((track) => track.stop());
      return;
    }

    screenRef.current = display;
    const screenTrack = display.getVideoTracks()[0];
    if (screenTrack && 'contentHint' in screenTrack) screenTrack.contentHint = 'detail';
    if (screenTrack) {
      await screenTrack
        .applyConstraints({
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 12, max: 12 },
        })
        .catch(() => undefined);
    }
    easyrtc.register3rdPartyLocalMediaStream(display, SCREEN_STREAM);

    // Presentation is the primary content. Keep the presenter's camera alive
    // for presence, but make it cheap enough that it cannot compete with text.
    const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
    if (cameraTrack) {
      for (const id of occupantsRef.current) {
        if (id !== myIdRef.current) {
          void tuneCameraSender(
            easyrtc,
            id,
            cameraTrack,
            Math.max(1, occupantsRef.current.length),
            true,
          );
        }
      }
    }

    // Renegotiate with everyone we are already connected to. Peers who join
    // later pick the share up through streamsToSend() instead.
    for (const id of occupantsRef.current) {
      if (id === myIdRef.current) continue;
      if (easyrtc.getConnectStatus(id) !== easyrtc.IS_CONNECTED) continue;
      easyrtc.addStreamToCall(id, SCREEN_STREAM, () => {
        if (screenTrack) {
          void tuneScreenSender(
            easyrtc,
            id,
            screenTrack,
            Math.max(1, occupantsRef.current.length),
          );
          window.setTimeout(
            () =>
              void tuneScreenSender(
                easyrtc,
                id,
                screenTrack,
                Math.max(1, occupantsRef.current.length),
              ),
            750,
          );
        }
      });
    }

    // The browser's own "Stop sharing" bar ends the track behind our back.
    // Guarded so our own stop path, which stops the track itself, cannot
    // re-enter and reopen the picker.
    display.getVideoTracks()[0]?.addEventListener(
      'ended',
      () => {
        if (screenRef.current === display) void toggleScreen();
      },
      { once: true },
    );

    setState((s) => ({ ...s, screenOn: true, screenStream: display }));
  }, []);

  return { ...state, toggleMic, toggleCam, toggleScreen, useDevice };
}
